import React from "react";

import Container from 'react-bootstrap/Container';
import Accordion from 'react-bootstrap/Accordion';

import "./FAQ.scss";

interface RateItem {
    type: "ONLINE" | "IN-OFFICE";
    price: string;
    title: string;
    detail: string;
}

const rates: RateItem[] = [
    {
        type: "ONLINE",
        price: "$260",
        title: "Online Initial Psychiatric Evaluation",
        detail: "New patients · 60 min",
    },
    {
        type: "IN-OFFICE",
        price: "$260",
        title: "In-Office Initial Psychiatric Evaluation",
        detail: "New patients · 60 min",
    },
    {
        type: "ONLINE",
        price: "$150",
        title: "Online Follow-Up Psychiatric Evaluation",
        detail: "Existing patients · 30 min",
    },
    {
        type: "IN-OFFICE",
        price: "$150",
        title: "In-Office Follow-Up Psychiatric Evaluation",
        detail: "Existing patients · 30 min",
    },
    {
        type: "ONLINE",
        price: "$170",
        title: "Online Initial Family Practice Evaluation",
        detail: "New patients · 30 min",
    },
    {
        type: "IN-OFFICE",
        price: "$170",
        title: "In-Office Initial Family Practice Evaluation",
        detail: "New patients · 30 min",
    },
    {
        type: "ONLINE",
        price: "$135",
        title: "Online Follow-Up Family Practice Evaluation",
        detail: "Existing patients · 20 min",
    },
    {
        type: "IN-OFFICE",
        price: "$135",
        title: "In-Office Follow-Up Family Practice Evaluation",
        detail: "Existing patients · 20 min",
    },
];

function RatesContent() {
    return (
        <>
            <p className="faq-rates-intro">
                Please inquire about our reduced rates if we do not currently take your insurance.
            </p>
            <div className="faq-rates-grid">
                {rates.map((rate) => (
                    <div key={rate.title} className="faq-rate-card">
                        <div className="faq-rate-card__top">
                            <span className="faq-rate-badge">{rate.type}</span>
                            <span className="faq-rate-price">{rate.price}</span>
                        </div>
                        <h4 className="faq-rate-title">{rate.title}</h4>
                        <p className="faq-rate-detail">{rate.detail}</p>
                    </div>
                ))}
            </div>
        </>
    );
}

function InsuranceContent() {
    const insurers = [
        { src: "../../aetna.webp", alt: "Aetna" },
        { src: "../../cigna.webp", alt: "Cigna" },
        { src: "../../uhc.webp", alt: "United Health Care" },
    ];

    const paymentMethods = ["Credit / debit", "HSA", "FSA"];

    return (
        <>
            <p className="faq-insurance-intro">
                We are currently taking Aetna, Cigna, and United Health Care insurances.
            </p>

            <div className="faq-insurance-logos">
                {insurers.map((insurer) => (
                    <div key={insurer.alt} className="faq-insurance-logo-card">
                        <div className="faq-insurance-logo-placeholder">
                            <img src={insurer.src} alt={insurer.alt} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="faq-self-pay">
                <h4 className="faq-self-pay__title">Self-pay &amp; Superbills</h4>
                <p className="faq-self-pay__body">
                    If you are self-pay, payment is expected before your visit with the provider. We'll provide a detailed receipt — a "superbill" — that you can submit to your insurance company for possible reimbursement. Please consult with your insurer with any questions prior to your visit.
                </p>
                <div className="faq-self-pay__accepted">
                    <span className="faq-self-pay__accepted-label">Accepted:</span>
                    {paymentMethods.map((method) => (
                        <span key={method} className="faq-payment-tag">{method}</span>
                    ))}
                </div>
            </div>
        </>
    );
}

const CLINIC_ADDRESS = "5757 S. Lindbergh Blvd, Suite 200, St. Louis, MO 63123";
const MAPS_EMBED_URL = `https://maps.google.com/maps?q=${encodeURIComponent(CLINIC_ADDRESS)}&hl=en&z=15&output=embed`;
const MAPS_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(CLINIC_ADDRESS)}`;

function LocationContent() {
    return (
        <>
            <p className="faq-body-text">
                We are located inside Revision Christian Counseling — {CLINIC_ADDRESS}.
            </p>
            <div className="faq-location-map">
                <iframe
                    title="Better Days Psychiatry and Family Practice location"
                    src={MAPS_EMBED_URL}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                />
            </div>
            <a
                className="faq-directions-button"
                href={MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
            >
                Get directions →
            </a>
        </>
    );
}

export default function FAQ() {
    return (
        <section className="faq-section">
            <Container>
                <header className="faq-header">
                    <h2 className="faq-title">Frequently asked questions</h2>
                    <hr className="faq-title-divider" />
                    <p className="faq-subtitle">
                        Everything about booking, rates, and visits. Can't find your answer? Reach out any time.
                    </p>
                </header>

                <Accordion className="faq-accordion">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Do I need a referral?</Accordion.Header>
                        <Accordion.Body>
                            <p className="faq-body-text">
                                Yes, a referral is currently required in order to be seen at our clinic. Please provide the name of the individual who referred you.
                            </p>
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="1">
                        <Accordion.Header>What are the current rates?</Accordion.Header>
                        <Accordion.Body>
                            <RatesContent />
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="2">
                        <Accordion.Header>What insurances do you accept?</Accordion.Header>
                        <Accordion.Body>
                            <InsuranceContent />
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="3">
                        <Accordion.Header>Where are you located?</Accordion.Header>
                        <Accordion.Body>
                            <LocationContent />
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="4">
                        <Accordion.Header>Can I see you virtually?</Accordion.Header>
                        <Accordion.Body>
                            <p className="faq-body-text">
                                Yes, we've partnered with Google Meets for secured telehealth. If you live anywhere in the state of Missouri, you can see us for your high quality mental and primary health care needs.
                            </p>
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="5">
                        <Accordion.Header>What ages and populations do you treat?</Accordion.Header>
                        <Accordion.Body>
                            <p className="faq-body-text">
                                We see all patients and populations from the age of 5  years old and up for psychiatric care.  We see patients from birth to geriatric for primary care.
                            </p>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <div className="faq-contact-cta">
                    <div className="faq-contact-cta__content">
                        <h3 className="faq-contact-cta__title">Still have questions?</h3>
                        <p className="faq-contact-cta__text">
                            Our team is happy to help.
                        </p>
                    </div>
                    <a
                        className="faq-contact-cta__button"
                        href="mailto:info@betterdayspractice.com?subject=Question%20from%20Better%20Days%20website"
                    >
                        Contact us →
                    </a>
                </div>
            </Container>
        </section>
    );
}
